/**
 * VerificationConfirmScreen
 *
 * Universal post-Sumsub confirm panel shown for EVERY person who completes
 * identity verification. Displays all 16 extracted fields across three
 * sections: Personal Details, Identity Document, and Residential Address.
 *
 * All fields are editable in-place (tap to edit). Required fields are
 * highlighted in destructive colour when empty.
 */

import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import type { ExtractedData } from './SumsubVerification';
import { resolveCountryName } from '@/utils/countryResolver';

export interface ConfirmedPersonData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  nationality: string;
  documentType: string;
  documentNumber: string;
  documentIssueDate: string;
  documentExpiry: string;
  documentCountry: string;
  street: string;
  city: string;
  postalCode: string;
  country: string;
  taxNumber: string;
}

interface Props {
  personName: string;
  personRole?: string;
  knownFirstName?: string;
  knownLastName?: string;
  email?: string;
  phone?: string;
  initialTaxNumber?: string;
  extractedData?: ExtractedData;
  onConfirm: (confirmed: ConfirmedPersonData) => void;
}

function buildInitialFields(
  extractedData: ExtractedData | undefined,
  personName: string,
  knownFirstName: string | undefined,
  knownLastName: string | undefined,
  email: string,
  phone: string,
  initialTaxNumber: string,
): ConfirmedPersonData {
  const doc = extractedData?.document;
  const addr = extractedData?.poa?.address || extractedData?.address;
  const parts = personName.trim().split(/\s+/);
  const fallbackFirst = knownFirstName || parts[0] || '';
  const fallbackLast = knownLastName || parts.slice(1).join(' ') || '';

  return {
    firstName: extractedData?.firstName || fallbackFirst,
    lastName: extractedData?.lastName || fallbackLast,
    dateOfBirth: extractedData?.dateOfBirth || '',
    email,
    phone,
    nationality: doc?.nationality ? resolveCountryName(doc.nationality) : '',
    documentType: doc?.documentType || '',
    documentNumber: doc?.documentNumber || '',
    documentIssueDate: doc?.documentIssueDate || '',
    documentExpiry: doc?.documentExpiry || '',
    documentCountry: doc?.documentCountry ? resolveCountryName(doc.documentCountry) : '',
    street: addr?.street || '',
    city: addr?.city || '',
    postalCode: addr?.postalCode || '',
    country: addr?.country ? resolveCountryName(addr.country) : '',
    taxNumber: initialTaxNumber || '',
  };
}

export function VerificationConfirmScreen({
  personName,
  personRole: _personRole,
  knownFirstName,
  knownLastName,
  email = '',
  phone = '',
  initialTaxNumber = '',
  extractedData,
  onConfirm,
}: Props) {
  const [fields, setFields] = useState<ConfirmedPersonData>(() =>
    buildInitialFields(extractedData, personName, knownFirstName, knownLastName, email, phone, initialTaxNumber)
  );

  const prevExtracted = useRef(extractedData);
  const prevInitialTaxNumber = useRef(initialTaxNumber);

  useEffect(() => {
    if (
      extractedData !== prevExtracted.current ||
      initialTaxNumber !== prevInitialTaxNumber.current
    ) {
      prevExtracted.current = extractedData;
      prevInitialTaxNumber.current = initialTaxNumber;
      setFields(buildInitialFields(extractedData, personName, knownFirstName, knownLastName, email, phone, initialTaxNumber));
    }
  }, [extractedData, initialTaxNumber, personName, knownFirstName, knownLastName, email, phone]);

  const [editingField, setEditingField] = useState<keyof ConfirmedPersonData | null>(null);

  const update = (field: keyof ConfirmedPersonData, value: string) => {
    setFields(prev => ({ ...prev, [field]: value }));
  };

  const renderField = (
    label: string,
    field: keyof ConfirmedPersonData,
    placeholder: string,
    required = false,
  ) => {
    const value = fields[field];
    const isEmpty = !String(value).trim();
    const showRequired = required && isEmpty;
    const isEditing = editingField === field;

    return (
      <div className="flex justify-between items-center py-2 border-b border-border/50 last:border-0">
        <span className={`text-sm shrink-0 pr-3 ${showRequired ? 'text-destructive' : 'text-muted-foreground'}`}>
          {label}{required ? '*' : ''}:
        </span>
        {isEditing ? (
          <Input
            value={value}
            onChange={e => update(field, e.target.value)}
            onBlur={() => setEditingField(null)}
            onKeyDown={e => e.key === 'Enter' && setEditingField(null)}
            className="w-1/2 h-7 text-right text-sm"
            placeholder={placeholder}
            autoFocus
          />
        ) : (
          <span
            className={`font-medium text-right cursor-pointer hover:text-primary transition-colors text-sm truncate max-w-[55%] ${showRequired ? 'text-destructive/70' : ''}`}
            onClick={() => setEditingField(field)}
          >
            {value || (
              <span className={showRequired ? 'text-destructive/70 italic' : 'text-muted-foreground italic'}>
                {placeholder}
              </span>
            )}
          </span>
        )}
      </div>
    );
  };

  const isValid =
    fields.firstName.trim() &&
    fields.lastName.trim() &&
    fields.email.trim();

  return (
    <div className="min-h-screen bg-background flex flex-col pt-20 pb-24 px-4 sm:px-6 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
        <div className="animate-fade-in-up">
          <h1 className="text-xl sm:text-2xl font-bold text-center tracking-[0.15em] mb-1">
            VERIFICATION SUMMARY
          </h1>
          <p className="text-muted-foreground text-center text-xs mb-5">
            Confirm the details extracted from the verification documents
          </p>

          <div className="rounded-xl border border-border bg-card p-4 mb-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-muted-foreground text-sm">Personal details</p>
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">Verified</span>
            </div>
            <div className="space-y-0">
              {renderField('First Name', 'firstName', 'Enter first name', true)}
              {renderField('Last Name', 'lastName', 'Enter last name', true)}
              {renderField('Date of Birth', 'dateOfBirth', 'YYYY-MM-DD')}
              {renderField('Telephone', 'phone', 'Enter telephone')}
              {renderField('Email', 'email', 'Enter email', true)}
              {renderField('Nationality', 'nationality', 'Enter nationality')}
              
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4 mb-3">
            <p className="text-muted-foreground text-sm mb-2">Identity Document</p>
            <div className="space-y-0">
              {renderField('Type of Document', 'documentType', 'e.g. Passport, ID Card')}
              {renderField('Document Number', 'documentNumber', 'Enter document number')}
              {renderField('Issue Date', 'documentIssueDate', 'YYYY-MM-DD')}
              {renderField('Expiry Date', 'documentExpiry', 'YYYY-MM-DD')}
              {renderField('Document Country', 'documentCountry', 'Issuing country')}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <p className="text-muted-foreground text-sm mb-2">Residential Address</p>
            <div className="space-y-0">
              {renderField('Address', 'street', 'Enter street address')}
              {renderField('City', 'city', 'Enter city')}
              {renderField('Post Code', 'postalCode', 'Enter post code')}
              {renderField('Country', 'country', 'Enter country')}
            </div>
          </div>

          <p className="text-xs text-muted-foreground mt-2 text-center">
            Tap any value to edit
          </p>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border safe-area-inset-bottom">
        <div className="max-w-md mx-auto">
          <Button
            onClick={() => onConfirm(fields)}
            disabled={!isValid}
            className="w-full h-14 text-base font-semibold rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Confirm & Continue
          </Button>
        </div>
      </div>
    </div>
  );
}
